import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPinIcon, BuildingOffice2Icon, UserIcon, PhoneIcon, GlobeAltIcon, AtSymbolIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';

const WatershedDetailPage = () => {
  const { watershedId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [farmData, setFarmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    fetchFarmDetails();
  }, [watershedId]);

  const fetchFarmDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getWatershedPublic(watershedId);

      if (response.success) {
        setFarmData(response.data);
      } else {
        setError(response.message || 'Failed to fetch watershed details');
        toast.error(response.message || 'Failed to fetch watershed details');
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while fetching watershed details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReportClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setShowReportModal(true);
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    toast.success('Thank you for your report. It has been submitted for review.');
  };

  const handleLoginRedirect = () => {
    setShowLoginPrompt(false);
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/login');
  };

  const InfoCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center mb-4">
        {Icon && <Icon className="h-6 w-6 text-primary-600 mr-2" />}
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, icon: Icon, isLink = false, linkText }) => (
    <div className="flex items-start py-2">
      {Icon && <Icon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />}
      <div className="flex-1">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 mt-1">
          {isLink && value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 underline break-all"
            >
              {linkText || value}
            </a>
          ) : (
            value || 'N/A'
          )}
        </dd>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading watershed details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Watershed Details</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/watersheds')}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            Back to Watersheds
          </button>
        </div>
      </div>
    );
  }

  if (!farmData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Watershed Not Found</h2>
          <p className="text-gray-600 mb-4">The requested watershed could not be found.</p>
          <button
            onClick={() => navigate('/watersheds')}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            Back to Watersheds
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => navigate(-1)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Back
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">Watershed Details</li>
            </ol>
          </nav>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {farmData.name}
              </h1>
              <p className="text-gray-600 mt-1">
                Detailed information about {farmData.name}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Update/Report Button */}
              <button
                onClick={handleReportClick}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors shadow-sm"
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Update
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Watershed Information */}
            <InfoCard title="Watershed Information" icon={BuildingOffice2Icon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Watershed Name" value={farmData.name} />
                <InfoRow label="Description" value={farmData.description} />
                <InfoRow label="Created By" value={farmData.created_by || 'N/A'} icon={UserIcon} />
                <InfoRow label="Total Area" value={farmData.area ? `${farmData.area} sq km` : 'N/A'} />
                <InfoRow label="Location" value={`${farmData.address}${farmData.state ? `, ${farmData.state}` : ''}, ${farmData.country}`} icon={MapPinIcon} />
                <InfoRow
                  label="Coordinates"
                  value={farmData.coordinates && (farmData.coordinates.latitude || farmData.coordinates.longitude) ?
                    `${farmData.coordinates.latitude}°N, ${farmData.coordinates.longitude}°E` :
                    'N/A'
                  }
                />
                <InfoRow label="Established" value={formatDate(farmData.created_at)} />
                <InfoRow label="Last Updated" value={formatDate(farmData.updated_at)} />
              </dl>
            </InfoCard>

            {/* Remove Social Media Links section - no longer in model */}
            {false && farmData.social_media_links && farmData.social_media_links.length > 0 && (
              <InfoCard title="Social Media">
                <div className="space-y-2">
                  {farmData.social_media_links.map((link, index) => (
                    <a
                      key={index}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-primary-600 hover:text-primary-700 underline break-all"
                    >
                      {link}
                    </a>
                  ))}
                </div>
              </InfoCard>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* QR Code Section */}
            {farmData.qr_code && (
              <InfoCard title="QR Code">
                <div className="text-center">
                  <img
                    src={farmData.qr_code}
                    alt="Watershed QR Code"
                    className="mx-auto h-32 w-32 border border-gray-200 rounded"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Scan to view this watershed's details
                  </p>
                </div>
              </InfoCard>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/watersheds')}
            className="bg-primary-600 text-white px-6 py-3 rounded-md hover:bg-primary-700 transition-colors font-medium"
          >
            ← Back to Watersheds
          </button>
        </div>
      </div>

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Report an Issue"
        size="xl"
      >
        {farmData && (
          <ReportForm
            watershedData={{ watershed: farmData }}
            onSuccess={handleReportSuccess}
            onCancel={() => setShowReportModal(false)}
          />
        )}
      </Modal>

      {/* Login Prompt Modal */}
      <Modal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Login Required"
        size="md"
        footer={
          <>
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLoginRedirect}
              className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              Login to Continue
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            You need to be logged in to report issues. Please log in to continue.
          </p>
          <p className="text-sm text-gray-500">
            After logging in, you'll be redirected back to this page to submit your report.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default WatershedDetailPage;